import React, { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import PropTypes from "prop-types"
import i18n from "i18n"
import { useProgress } from "hooks/useProgress"
import { useNavigate } from "react-router-dom"
import * as amplitude from "@amplitude/analytics-browser"

const SupportMessage = () => (
  <Typography
    sx={{
      color: "rgba(65, 65, 65, 0.7)",
      textAlign: "center",
      mt: 2,
      lineHeight: "18px",
    }}
  >
    {"¿Crees que ha habido algún problema en la importación? Escríbenos a "}
    <a
      href="mailto:soporte@planhopper.com"
      style={{ textDecoration: "underline" }}
    >
      soporte@planhopper.com
    </a>
  </Typography>
)

const WaitingModal = ({
  openModal,
  setOpenModal,
  setSuccessModal,
  setSeleccionado,
  project,
  imTemplate,
  deleteProject,
  setUploadModal,
}) => {
  const navigate = useNavigate()
  const importedProject = useProgress({
    projectId: project.projectId,
    accountId: project.createdBy,
  })

  // "loading" | "success" | "error"
  const [status, setStatus] = useState("loading")

  useEffect(() => {
    if (!importedProject) return
    if (importedProject.importStatus === "COMPLETED") {
      amplitude.track("BC3 Imported")
      setStatus("success")
    } else if (
      importedProject.importStatus === "FAILED" ||
      importedProject.importStatus === "ERROR"
    ) {
      setStatus("error")
    }
  }, [importedProject])

  const handleGoToBudget = () => {
    if (imTemplate) {
      navigate("/bancodeprecios/templateV2/" + importedProject.id)
    } else {
      navigate("/budgeting/" + importedProject.id)
    }
  }

  const handleClose = () => {
    setOpenModal(false)
  }

  const handleRetry = () => {
    setOpenModal(false)
    setUploadModal(true)
  }

  const handleGoBack = async () => {
    if (deleteProject && project?.projectId) {
      await deleteProject(project.projectId)
    }
    setOpenModal(false)
  }

  return (
    <Dialog
      open={openModal}
      onClose={null}
      PaperProps={{
        sx: { borderRadius: "8px", width: "500px", maxWidth: "500px", p: 1 },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 1, px: 4, textAlign: "center" }}>
        {status === "loading" && (
          <>
            <CircularProgress
              size={56}
              thickness={4}
              sx={{ color: "branded.main", mb: 3 }}
            />
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 500,
                fontFamily: "Helvetica",
                color: "#414141",
                mb: 1,
              }}
            >
              {i18n.t("Estamos construyendo tu")}
              <br />
              {i18n.t("presupuesto y planning...")}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 300,
                fontFamily: "Helvetica",
                color: "rgba(65,65,65,0.7)",
                lineHeight: "20px",
              }}
            >
              {i18n.t(
                "Esto puede tomar un minuto. Por favor no te vayas, ni cierres esta pestaña",
              )}
              <br />
              {i18n.t("Creando")}{" "}
              <strong>{importedProject.chapters ?? 0}</strong>{" "}
              {i18n.t("capítulos con un total de")}{" "}
              <strong>{importedProject.parts ?? 0}</strong> {i18n.t("partidas")}
            </Typography>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 64, color: "#52C41A", mb: 2 }}
            />
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 500,
                fontFamily: "Helvetica",
                color: "#414141",
                mb: 1,
              }}
            >
              {i18n.t("¡Importación completada!")}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 300,
                fontFamily: "Helvetica",
                color: "rgba(65,65,65,0.7)",
                lineHeight: "20px",
              }}
            >
              {i18n.t("Se han importado")}{" "}
              <strong>{importedProject.chapters ?? 0}</strong>{" "}
              {i18n.t("capítulos y")}{" "}
              <strong>{importedProject.parts ?? 0}</strong>{" "}
              {i18n.t("partidas correctamente.")}
            </Typography>
            <SupportMessage />
          </>
        )}

        {status === "error" && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 64, color: "#FF4D4F", mb: 2 }} />
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 500,
                fontFamily: "Helvetica",
                color: "#414141",
                mb: 1,
              }}
            >
              {i18n.t("Algo no va bien...")}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 300,
                fontFamily: "Helvetica",
                color: "rgba(65,65,65,0.7)",
                lineHeight: "20px",
              }}
            >
              {i18n.t(
                "No hemos podido procesar tu archivo BC3. Por favor, inténtalo de nuevo o contacta con soporte si el problema persiste.",
              )}
            </Typography>
            <SupportMessage />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: "center", gap: 2, px: 4, pb: 4, pt: 2 }}
      >
        {status === "success" && (
          <>
            <Button
              variant="outlined"
              color="branded"
              onClick={handleClose}
              sx={{
                textTransform: "none",
                fontFamily: "Helvetica",
                fontWeight: 600,
              }}
            >
              {i18n.t("Volver")}
            </Button>
            <Button
              variant="contained"
              color="branded"
              onClick={handleGoToBudget}
              sx={{
                textTransform: "none",
                fontFamily: "Helvetica",
                fontWeight: 600,
              }}
            >
              {i18n.t("Ir al presupuesto")}
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <Button
              variant="outlined"
              color="branded"
              onClick={handleGoBack}
              sx={{
                textTransform: "none",
                fontFamily: "Helvetica",
                fontWeight: 600,
              }}
            >
              {i18n.t("Volver")}
            </Button>
            <Button
              variant="contained"
              color="branded"
              onClick={handleRetry}
              sx={{
                textTransform: "none",
                fontFamily: "Helvetica",
                fontWeight: 600,
              }}
            >
              {i18n.t("Volver a intentarlo")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

WaitingModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  setSuccessModal: PropTypes.func.isRequired,
  setSeleccionado: PropTypes.func.isRequired,
  project: PropTypes.object,
  imTemplate: PropTypes.bool,
  deleteProject: PropTypes.func,
  setUploadModal: PropTypes.func,
}

export default WaitingModal
